import { useDebouncedValue } from '@mantine/hooks';
import { Loader } from 'src/design/atoms/Loader';
import { Select } from 'src/design/atoms/Select';
import React from 'react';
import { ClientGpDto } from 'src/api/FiretrolApiClient';
import { useClientSearch } from 'src/api/queries/organization/useClientSearch';

export interface IClientSelectorProps {
  onChange: (client: ClientGpDto | undefined) => void;
  initialValue?: ClientGpDto | undefined;
  clientOptions?: ClientGpDto[];
}

/** @registry Select dropdown of clients. */
export const ClientSelector: React.FC<IClientSelectorProps> = ({
  onChange,
  initialValue,
  clientOptions = undefined,
}) => {
  const [selected, setSelected] = React.useState<ClientGpDto | undefined>(
    initialValue,
  );
  const [searchValue, setSearchValue] = React.useState('');
  const [debouncedSearch] = useDebouncedValue(searchValue, 300);

  const { data: searchResults, isFetching } = useClientSearch(
    debouncedSearch,
    !clientOptions,
  );

  React.useEffect(() => {
    if (clientOptions) {
      setSelected(clientOptions[0] || undefined);
    }
  }, [clientOptions]);

  React.useEffect(() => {
    onChange(selected);
  }, [selected]);

  const onValueChange = (value: ClientGpDto | undefined) => {
    setSelected(value);
  };

  const clients = clientOptions || searchResults || [];

  const data = React.useMemo(() => {
    return clients.map((client) => ({
      value: client.custNumber,
      label: client.custName,
    }));
  }, [clients]);

  const handleChange = (selectedValue: string | null) => {
    if (selectedValue) {
      const selectedClient = clients.find(
        (c) => c.custNumber === selectedValue,
      );
      onValueChange(selectedClient);
    } else {
      onValueChange(undefined);
    }
  };

  const loading = !clientOptions && isFetching;

  return (
    <Select
      placeholder="Select a customer..."
      data={data}
      value={selected?.custNumber || null}
      onChange={handleChange}
      onSearchChange={!clientOptions ? setSearchValue : undefined}
      searchable
      rightSection={!loading ? undefined : <Loader color="blue" size="xs" />}
      clearable
      nothingFoundMessage={loading ? 'Loading...' : 'No customers found'}
    />
  );
};
